import type {
  ConfigurationTraceLevel,
  ConfigurationTraceResponse
} from '@/domains/configuration/types/responses/ConfigurationTraceResponse.types'
import type { ExoStatus } from '@/domains/fiscal/types/consts/ExoStatus.consts'
import type { ExoLocationRow, ExoSetup } from '@/domains/fiscal/utils/exo-setup/buildExoSetup.types'
import type { LocationModel } from '@/domains/locations/types/models/LocationModel.types'

import { isExoStatus } from '@/domains/fiscal/types/consts/ExoStatus.consts'

/**
 * Build the EXO setup view model from one unscoped configuration trace plus the locations list.
 *
 * The trace is requested with no scope on purpose: with a null context model the `location` level
 * is not in the resolution chain, so `GetConfigurationTraceAction` excludes nothing from its
 * sibling query and `sibling_overrides` comes back holding every location that has stored a value
 * for the key. That is what keeps this to a constant two requests instead of one per location.
 */
const LOCATION_LEVEL = 'location'

const TENANT_LEVEL = 'tenant'

const buildExoSetup = (trace: ConfigurationTraceResponse, locations: LocationModel[]): ExoSetup => {
  const locationLevel = findLevel(trace, LOCATION_LEVEL)
  const tenantStatus = asStatus(findLevel(trace, TENANT_LEVEL)?.chain_override?.value)

  const overrides = new Map<string, ExoStatus | null>(
    (locationLevel?.sibling_overrides ?? [])
      .filter(override => override.scope_id !== null)
      .map(override => [String(override.scope_id), asStatus(override.value)])
  )

  const isTruncated = locationLevel?.is_truncated ?? false
  const overrideTotal = locationLevel?.sibling_overrides_total ?? 0
  const namesById = new Map(locations.map(location => [String(location.id), location.name]))

  return {
    tenantStatus,
    rows: isTruncated
      ? overrideRows(overrides, namesById)
      : allLocationRows(overrides, namesById, locations, tenantStatus),
    isTruncated,
    overrideTotal
  }
}

const findLevel = (trace: ConfigurationTraceResponse, level: string): ConfigurationTraceLevel | undefined =>
  trace.levels.find(candidate => candidate.level === level)

/** Config values persist as JSON scalars; anything outside the enum reads as unset. */
const asStatus = (value: unknown): ExoStatus | null => (isExoStatus(value) ? value : null)

/**
 * Truncated: list only the overrides the server actually returned. Locations absent from a partial
 * list cannot be called inherited, so they are left out entirely and the caller says how many.
 */
const overrideRows = (overrides: Map<string, ExoStatus | null>, namesById: Map<string, string>): ExoLocationRow[] =>
  [...overrides.entries()].map(([scopeId, status]) => ({
    key: scopeId,
    name: nameFor(scopeId, namesById),
    status,
    isInherited: false
  }))

/**
 * Complete: every location, plus any override pointing at a location the index didn't return (a
 * deleted one, say) so a stored value never silently disappears from the list.
 */
const allLocationRows = (
  overrides: Map<string, ExoStatus | null>,
  namesById: Map<string, string>,
  locations: LocationModel[],
  tenantStatus: ExoStatus | null
): ExoLocationRow[] => {
  const rows = locations.map(location => {
    const scopeId = String(location.id)
    const hasOverride = overrides.has(scopeId)

    return {
      key: scopeId,
      name: location.name,
      status: hasOverride ? (overrides.get(scopeId) ?? null) : tenantStatus,
      isInherited: !hasOverride
    }
  })

  const orphans = [...overrides.entries()]
    .filter(([scopeId]) => !namesById.has(scopeId))
    .map(([scopeId, status]) => ({
      key: scopeId,
      name: nameFor(scopeId, namesById),
      status,
      isInherited: false
    }))

  return [...rows, ...orphans]
}

const nameFor = (scopeId: string, namesById: Map<string, string>): string =>
  namesById.get(scopeId) ?? `Location ${scopeId}`

export { buildExoSetup }
